import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import axios from 'axios';
import {
  IoPersonOutline,
  IoWalletOutline,
  IoLinkOutline,
  IoMailOutline,
  IoPeopleOutline,
  IoStatsChartOutline,
  IoCashOutline,
  IoCalendarOutline,
  IoShieldCheckmarkOutline
} from 'react-icons/io5';
import LevelUserListModal from './LevelUserListModal';
import './UserDetailModal.css';

export default function UserDetailModal({ walletAddress, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const environment = process.env.REACT_APP_ENVIRONMENT || 'development';
  const BASE_URL = environment === 'production'
    ? process.env.REACT_APP_PROD_API_URL
    : environment === 'staging'
      ? process.env.REACT_APP_STAG_API_URL
      : process.env.REACT_APP_DEV_API_URL || 'http://localhost:4001';

  useEffect(() => {
    if (walletAddress) {
      setLoading(true);
      axios.get(`${BASE_URL}/v1/validator-delegator/stats/${walletAddress}`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("acessToken")}` }
      })
        .then(res => setStats(res.data.data))
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [walletAddress, BASE_URL]);

  if (!walletAddress) return null;

  const infoBox = (label, value, icon, color = "text-dark") => (
    <div className="bg-light p-3 rounded-3 border h-100 shadow-sm transition-hover">
      <div className="text-muted fw-bold x-small d-flex align-items-center gap-2 text-uppercase mb-1" style={{ fontSize: '10px' }}>
        {icon} {label}
      </div>
      <div className={`fw-bold ${color}`} style={{ fontSize: '14px', wordBreak: 'break-all' }}>
        {loading ? <span className="placeholder-glow"><span className="placeholder col-6"></span></span> : (value !== undefined && value !== null ? value : "N/A")}
      </div>
    </div>
  );

  return (
    <Modal
      show={!!walletAddress}
      onHide={onClose}
      centered
      size="xl"
      scrollable={true}
      contentClassName="user-detail-modal-content border-0 shadow-lg"
      style={{ backdropFilter: 'blur(5px)' }}
    >
      <Modal.Header closeButton className="border-0 py-3 px-4" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
        <Modal.Title className="fs-5 fw-bold text-primary d-flex align-items-center gap-2">
          <IoPersonOutline size={24} />
          Invest User Profile Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-white">
        <div className="row g-4">

          {/* Identity Section */}
          <div className="col-12 col-lg-4">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-secondary">
              <IoShieldCheckmarkOutline /> Identity Info
            </h6>
            <div className="d-flex flex-column gap-3">
              {infoBox("Username", stats?.identity?.username, <IoPersonOutline />)}
              {infoBox("Email Address", stats?.identity?.email, <IoMailOutline />)}
              {infoBox("Wallet Address", walletAddress, <IoWalletOutline />, "font-monospace small")}
              <div className="row g-2">
                <div className="col-6">
                  {infoBox("My Code", stats?.identity?.referralCode, <IoLinkOutline />, "text-primary")}
                </div>
                <div className="col-6">
                  {infoBox("Level", stats?.identity?.level !== undefined ? `Level ${stats.identity.level}` : null, <IoStatsChartOutline />, "text-success")}
                </div>
              </div>
            </div>
          </div>

          {/* Investment & Referral Section */}
          <div className="col-12 col-lg-4">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-secondary">
              <IoCashOutline /> Investment Stats
            </h6>
            <div className="d-flex flex-column gap-3">
              <div className="bg-primary bg-opacity-10 p-4 rounded-3 border border-primary border-opacity-25 text-center shadow-sm">
                <div className="text-primary fw-bold text-uppercase mb-1" style={{ fontSize: '12px' }}>Total Amount Invested</div>
                <div className="fs-2 fw-bold text-primary">
                  {loading ? "..." : `$${(stats?.investment?.totalUsdt || 0).toFixed(2)}`}
                </div>
              </div>

              <div className="row g-2">
                <div className="col-6">
                  {infoBox("Validator Stakes", stats?.investment?.validatorStakes ?? 0, <IoShieldCheckmarkOutline />)}
                </div>
                <div className="col-6">
                  {infoBox("Delegator Stakes", stats?.investment?.delegatorStakes ?? 0, <IoPeopleOutline />)}
                </div>
              </div>

              {infoBox("Referred By", stats?.referrer?.username ? `${stats.referrer.username} (${stats.identity.parentReferralCode})` : "Organic Join", <IoLinkOutline />)}
              {infoBox("Join Date", stats?.identity?.joinDate ? new Date(stats.identity.joinDate).toLocaleString() : null, <IoCalendarOutline />)}
            </div>
          </div>

          {/* Network Section */}
          <div className="col-12 col-lg-4">
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-secondary">
              <IoPeopleOutline /> Network Downline (5-Level)
            </h6>
            <div className="bg-light p-3 rounded-4 border shadow-sm h-100">
              {/* Summary Cards */}
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div className="bg-white p-2 rounded-3 border text-center shadow-sm">
                    <div className="text-muted x-small fw-bold text-uppercase" style={{ fontSize: '9px' }}>Validators (No.)</div>
                    <div className="fw-bold text-primary">{loading ? "..." : (stats?.downlineStats?.totalValidator || 0)}</div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="bg-white p-2 rounded-3 border text-center shadow-sm">
                    <div className="text-muted x-small fw-bold text-uppercase" style={{ fontSize: '9px' }}>Delegators (No.)</div>
                    <div className="fw-bold text-info">{loading ? "..." : (stats?.downlineStats?.totalDelegator || 0)}</div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="bg-white p-2 rounded-3 border text-center shadow-sm">
                    <div className="text-muted x-small fw-bold text-uppercase" style={{ fontSize: '9px' }}>Purchase</div>
                    <div className="fw-bold text-success" style={{ fontSize: '11px' }}>
                      {loading ? "..." : `$${(stats?.downlineStats?.totalPurchase || 0).toLocaleString()}`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column gap-3">
                {[1, 2, 3, 4, 5].map(lvl => (
                  <div
                    key={lvl}
                    className="d-flex justify-content-between align-items-center p-2 px-3 bg-white rounded-3 border shadow-sm cursor-pointer transition-hover"
                    onClick={() => setSelectedLevel(lvl)}
                  >
                    <span className="fw-bold text-muted small">Level {lvl}</span>
                    <span className="badge bg-primary rounded-pill px-3 py-2">
                      {loading ? "..." : (stats?.network?.downlineLevels?.[lvl] || 0)} Users
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </Modal.Body>

      <LevelUserListModal
        show={selectedLevel !== null}
        onClose={() => setSelectedLevel(null)}
        walletAddress={walletAddress}
        level={selectedLevel}
      />
    </Modal>
  );
}
